import { BehaviorSubject, Observable, Subject, distinctUntilChanged, map } from 'rxjs';

export interface LoadingState<T> {
    value: T;
    loading: boolean;
}

export interface DraftPublishedState<T> {
    published: LoadingState<T>;
    draft: LoadingState<T>;
}

// help class for handeling states for a variable
export abstract class Store<T> {
    protected _ngStateUnsubscribe$ = new Subject<void>();
    // for all stores: create specific getState-functions
    // that return partial states and states with throttle and other settings to the components using it
    protected get state$(): BehaviorSubject<T> {
        return this._state$;
    };

    private _state$: BehaviorSubject<T>;

    constructor(initialState?: T) {
        this._state$ = new BehaviorSubject(initialState);
    }

    public get stateSnapshot(): T {
        return this._state$.getValue();
    }

    public resetListener() {
        this._ngStateUnsubscribe$.next();
        this._ngStateUnsubscribe$.complete();
        this._ngStateUnsubscribe$ = new Subject();
    }

    protected setState(nextState: T): void {
        this._state$.next(nextState);
    }

    protected patchState(nextState: Partial<T>): void {
        this._state$.next({ ...this._state$.getValue(), ...nextState });
    }

    protected select<K>(mapFn: (state: T) => K): Observable<K> {
        return this.state$.asObservable().pipe(
            map((state: T) => mapFn(state)),
            distinctUntilChanged()
        );
    }
}
